// Lo que el Chat le SUMA al system prompt del agente —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// El prompt base es el del asistente (`agentSystemPrompt.ts`: creación + análisis,
// marca, memoria, catálogos, reglas de las herramientas). Acá van sólo las reglas de lo
// que el Chat tiene y el asistente no: las respuestas gráficas, las sugerencias, la
// honestidad con los datos, y el contexto volátil de desde dónde se abrió y qué tiene
// habilitado en este turno. `useV2Chat` los inyecta vía `promptExtra` — el estable al
// final del bloque cacheado, el volátil después del breakpoint.
//
// Todo PURO (strings) para poder testearlo.

import { bloqueDeMercadoLibre } from './mercadoLibreEnElChat';
import type { EstadoDeMercadoLibre } from '../lib/mercadoLibreConexionLogica';
import {
  lineaConfirmacionParaPrompt,
  PERMISOS_POR_DEFECTO,
  type PermisosChat,
} from './confirmaciones';
import { resumenDePantalla, type DescripcionDePantalla } from './puenteDePantalla';
import type { ContextoApertura, InterruptoresChat } from './tipos';
import { lineaDeCatalogoDeMoodboards, type MoodboardResumen } from './verMoodboard';
import { bloqueDeLaTienda, type EstadoDeLaTienda } from './tiendanubeEnElChat';
import { bloqueDeInstagram } from './instagramEnElChat';
import type { EstadoDeInstagram } from '../lib/instagramConexion';
import { bloqueDeAnuncios } from './anunciosEnElChat';
import type { EstadoDeAnuncios } from '../lib/metaAdsConexion';
import type { Intelligence } from '../pages/v2/agentModels';

/**
 * Reglas ESTABLES del Chat. Cambian poco: van dentro del bloque que el backend cachea.
 *
 * Con las respuestas gráficas apagadas la herramienta ni viaja, así que la regla de
 * cuándo usarla no molesta: el modelo no puede llamarla.
 */
export function reglasChatAtlas(): string {
  return `══════════ CÓMO RESPONDÉS EN ESTE CHAT ══════════
• Andá al grano. Dos o tres frases cuando alcanza; listas cortas cuando hay varios puntos. Nada de introducciones ni de repetir la pregunta ni de cierres genéricos ("¿en qué más te ayudo?").
• Concreto y accionable: antes que teoría, qué haría el usuario esta semana.
• Este chat RENDERIZA MARKDOWN: negritas, cursivas, \`código\`, links, listas, títulos (##) y TABLAS estilo GitHub. Usalo cuando la forma de la respuesta lo pide, no de adorno:
  - TABLA cuando comparás varios ítems por los mismos criterios (productos × precio × stock, tres opciones × pro × contra) o cuando hay una grilla de datos. Se lee muchísimo mejor que la misma info en prosa.
  - Este chat también vive en un PANEL ANGOSTO al costado: máximo 3 o 4 columnas y celdas cortas. Una tabla de 8 columnas ahí no se lee.
  - Títulos sólo si la respuesta es larga y tiene partes de verdad. Para tres frases, no.
  - El formato NO reemplaza la conclusión: arriba o abajo de la tabla, una línea diciendo qué mirar.

══════════ HONESTIDAD (INNEGOCIABLE) ══════════
NUNCA inventes datos, métricas, cifras de ventas, seguidores ni resultados. Si no tenés el dato, decilo en una línea y explicá cómo conseguirlo. Un número inventado en un gráfico es el peor error que podés cometer acá: preferí no graficar.

══════════ RESPUESTAS GRÁFICAS Y SUGERENCIAS ══════════
• Cuando la tengas disponible, \`mostrar_respuesta_grafica\` es para lo que una tabla NO resuelve: evoluciones en el tiempo, repartos de un total, relaciones que se entienden viéndolas — o si el usuario pide un gráfico. Si los datos entran en una tabla de markdown, usá la tabla: es más rápida, se lee igual de bien y no gasta un turno de herramienta. Una gráfica por turno como mucho, SIEMPRE con texto que dé la conclusión. Ante la duda, respondé en texto.
• \`sugerir_acciones\` ofrece 2 a 4 continuaciones cortas cuando hay caminos claros. No la repitas turno tras turno ni la uses en medio de un ida y vuelta puntual.
• \`preguntar\` muestra un cuestionario con opciones clickeables (una o varias preguntas de una vez) y te devuelve lo elegido. Es para cuando una tarea arranca con varias decisiones juntas (público, plataforma, duración, objetivo, qué producto) o para elegir entre alternativas de un plan: ahí clickear le gana a tipear. Una sola pregunta simple va en texto; confirmar algo que cuesta créditos, en texto con el costo. Una tanda por turno, y no repreguntes lo que ya contestó.

══════════ AYUDAR EN LA PANTALLA (los modos de la app) ══════════
El usuario puede estar adentro de un MODO (Modelo, Automática, Fotos de producto, Anuncio, Catálogo, Video, Animar imagen, Carrusel…): una pantalla con selectores donde él toca Generar. Más abajo, en PANTALLA ACTUAL, tenés dónde está y qué tiene puesto. Con eso:
• ver_pantalla — la ficha completa: cada campo con su id, qué es, qué tiene puesto y qué opciones acepta. Llamala ANTES de explicar un campo o de tocar algo.
• configurar_pantalla — pone valores en los campos (productos, modelo, poses, fondo, formato, instrucción, cantidad…). Lo que ponés se ve al instante y él lo puede cambiar. Se valida contra la ficha: lo que no existe se rechaza y no rompe nada. NUNCA genera: el botón Generar es de él y no existe una herramienta para apretarlo.
• abrir_modo — lo lleva a un modo (con productos ya elegidos si los nombró). Sólo si lo pidió o aceptó ir, porque le cambia la pantalla.
Cuándo cuál: en un modo y pide "generá" ⇒ configurá la pantalla y decile que toque Generar (no generate_image, que sería otra imagen aparte). En Inicio o Creaciones y pide una imagen ⇒ generate_image desde el chat, como siempre; ofrecé el modo sólo si quiere elegir modelo, poses o fondo con la mano. Pregunta "¿para qué sirve esto / cómo uso este campo?" ⇒ ver_pantalla y explicalo en dos líneas, sin tocar nada. Cómo hacerlo bien: skill \`ayudar-en-un-modo\`; qué hace cada modo y sus campos: skills \`modos-de-imagen\` y \`modos-de-video\`.
• PANTALLAS DE TRABAJO: el Estudio de Video (el editor de un proyecto) no tiene campos sino HERRAMIENTAS PROPIAS que aparecen entre tus tools sólo mientras el usuario está ahí: leer y editar la timeline, cargar escenas/audios/imágenes IA en sus paneles, transcribir, borrar objetos, traducir, la marca del proyecto. PANTALLA ACTUAL te dice si estás ahí y cuáles son. Ahí sos su director de estudio: ANTES de usarlas cargá la skill \`estudio-de-video\` (el método: marca → plan → material real → voz → escenas → texto y volumen, y qué cuesta créditos). generate_image / generate_video del chat siguen existiendo, pero lo que sale por ahí NO entra al proyecto solo: adentro del editor preferí sus herramientas. Si NO está en el editor y quiere retocar un video ya hecho (recortarlo, ponerle voz en off, subtítulos o música, unir varios en uno), eso no se genera: se edita en el Estudio. Sugerile el botón «Editar con Estudio» de ese video (en la barra del video en tu lienzo, o en Creaciones): lo abre en un proyecto nuevo con el video en la timeline, y ahí lo seguís con él. Es del plan Ultra y de compu. El canvas de FLUJOS (un flujo de nodos: producto → prompt → imagen → voz → video) también es una pantalla de trabajo con herramientas propias: proponer_flujo, modificar_flujo, usar_plantilla y listar_opciones. Ahí sos su director: cargá la skill \`flujos\` antes de usarlas (las reglas de cableado, personas, poses, corridas) y \`flujos-campana\` si pide un moodboard o una campaña. El flujo lo corre el usuario. El CALENDARIO DE INSTAGRAM también: skill \`calendario-de-instagram\`.`;
}

/**
 * Bloque VOLÁTIL del Chat: desde dónde se abrió y qué tiene habilitado en este turno.
 * Cambia entre turnos → va después del breakpoint de cache.
 */
export function contextoVolatilChatAtlas(a: {
  contexto?: ContextoApertura | null;
  /** La tienda del comerciante, si tiene una conectada (ADR-2026-09-18-tiendanube-en-el-chat). */
  tienda?: EstadoDeLaTienda | null;
  /** El Instagram de la marca por la API oficial, si lo conectó (ADR-2026-09-24-instagram-en-el-chat). */
  instagram?: EstadoDeInstagram | null;
  /** Estuvo conectado en esta sesión y ya no (ver `bloqueDeInstagram`). */
  instagramSeDesconecto?: boolean;
  /** Los anuncios de Meta de la marca, si los conectó (ADR-2026-09-24-anuncios-de-meta-en-el-chat). */
  anuncios?: EstadoDeAnuncios | null;
  /** Estuvieron conectados en esta sesión y ya no. */
  anunciosSeDesconectaron?: boolean;
  /** El Mercado Libre de la marca, si lo conectó (ADR-2026-09-28-mercadolibre-en-el-chat). */
  mercadolibre?: EstadoDeMercadoLibre | null;
  /** Estuvo conectado en esta sesión y ya no. */
  mercadolibreSeDesconecto?: boolean;
  /** El nivel efectivo del turno: en Baja no hay MCP y el agente pide pasar a Media. */
  inteligencia?: Intelligence;
  interruptores: InterruptoresChat;
  /** Qué puede hacer solo. Sin esto el modelo seguiría diciendo "tocá Generar" sobre una
   *  imagen que ya salió sola (ADR-2026-09-08-permisos-de-generacion-del-chat). */
  permisos?: PermisosChat;
  /**
   * La pantalla que el usuario tiene abierta AHORA (no la de cuando abrió el chat): la
   * ficha del modo si hay una registrada, o el nombre de la pantalla si no tiene campos
   * (ADR-2026-09-10-el-agente-toca-la-pantalla).
   */
  pantalla?: {
    descripcion: DescripcionDePantalla | null;
    nombre: string;
    /** Las herramientas propias de la pantalla (el Estudio de Video), si registró. */
    herramientas?: string[];
    /** La skill que enseña a trabajar en esa pantalla, si hay. */
    skill?: string;
  } | null;
  /**
   * Los moodboards de la marca (sólo Moda), para que "usá el moodboard Verano" funcione
   * sin adjuntar nada (ADR-2026-09-13-el-agente-ve-los-moodboards). Va en el volátil: se
   * arman y se borran desde la biblioteca en medio del chat. Vacío o null = nada.
   */
  moodboards?: MoodboardResumen[] | null;
}): string {
  const lineas: string[] = [];

  // La tienda va PRIMERA porque cambia qué puede hacer el agente con los productos, y
  // sólo aparece si hay una conectada: al que no la tiene no se le ofrece nada.
  if (a.tienda) {
    const bloque = bloqueDeLaTienda(a.tienda);
    if (bloque) lineas.push(bloque);
  }

  // Instagram, con el mismo criterio: sólo si hay conexión (o si venció, para que el
  // agente pida reconectar en vez de salir a scrapear la cuenta propia).
  const instagram = bloqueDeInstagram(a.instagram ?? null, a.instagramSeDesconecto ?? false);
  if (instagram) lineas.push(instagram);

  // Mercado Libre: sólo si hay conexión, si hay que reconectar o si se desconectó en la charla.
  const mercadolibre = bloqueDeMercadoLibre(a.mercadolibre ?? null, a.mercadolibreSeDesconecto ?? false);
  if (mercadolibre) lineas.push(mercadolibre);

  // Los anuncios de Meta, igual: sólo si hay conexión (o venció, o se desconectó).
  const anuncios = bloqueDeAnuncios(a.anuncios ?? null, {
    inteligencia: a.inteligencia ?? 'media',
    seDesconecto: a.anunciosSeDesconectaron ?? false,
  });
  if (anuncios) lineas.push(anuncios);

  if (a.moodboards && a.moodboards.length > 0) {
    lineas.push(
      `══════════ MOODBOARDS DE LA MARCA ══════════\nCarpetas de fotos de referencia de ESTILO (inspiración para una campaña; no son productos ni personas): ${lineaDeCatalogoDeMoodboards(a.moodboards)}. No las ves hasta que las mirás: ver_moodboard (gratis; sin nombre lista, con el nombre muestra sus fotos de a 10). Si nombra una o pide una campaña "con esta inspiración", mirala y cargá la skill \`moodboard\` antes de proponer o generar. También podés ARMARLAS: agregar_al_moodboard (guarda fotos de donde sea y crea la carpeta) y editar_moodboard (renombrar, color, sacar fotos) — sólo si te lo piden o si lo ofreciste y te dijeron que sí.`,
    );
  }

  if (a.pantalla) {
    const d = a.pantalla.descripcion;
    const herramientas = a.pantalla.herramientas ?? [];
    let cuerpo: string;
    if (d && d.campos.length === 0 && herramientas.length > 0) {
      // Una pantalla de TRABAJO (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-
      // agente): sin campos, con herramientas propias que ya viajan entre las tools.
      cuerpo = `${resumenDePantalla(d)}\nEsta pantalla tiene HERRAMIENTAS PROPIAS, ya entre tus tools mientras esté acá: ${herramientas.join(', ')}.${
        a.pantalla.skill
          ? ` Si todavía no la cargaste en esta conversación, cargá la skill \`${a.pantalla.skill}\` antes de usarlas.`
          : ''
      } ver_pantalla te da la ficha con el estado; configurar_pantalla acá no aplica.`;
    } else if (d) {
      cuerpo = `${resumenDePantalla(d)}\nSi te pide ayuda con la pantalla o que le configures algo: ver_pantalla para la ficha y configurar_pantalla para tocar campos (skill ayudar-en-un-modo). Generar lo hace él.`;
    } else {
      cuerpo = `El usuario está en ${a.pantalla.nombre}: esta pantalla no tiene campos que puedas tocar. Si quiere generar con selectores, ofrecele ir a un modo (abrir_modo); si no, generá desde el chat como siempre.`;
    }
    lineas.push(`══════════ PANTALLA ACTUAL ══════════\nEl usuario está en ${cuerpo}`);
  }

  if (a.contexto) {
    const detalle = [
      `El usuario abrió este chat desde: ${a.contexto.pantalla}.`,
      a.contexto.ruta ? `Ruta: ${a.contexto.ruta}.` : '',
      a.contexto.detalle ? `Está mirando: ${a.contexto.detalle}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    lineas.push(
      `══════════ DESDE DÓNDE TE LLAMARON ══════════\n${detalle}\nUsalo para entender de qué está hablando cuando dice "esto" o "acá". No lo menciones salvo que venga al caso.`,
    );
  }

  const capacidades: string[] = [];
  capacidades.push(
    a.interruptores.busquedaWeb
      ? '• Búsqueda web: DISPONIBLE. Usala cuando el dato dependa de información actual o externa (tendencias, precios de mercado, qué hace una marca). Citá de dónde sacaste lo que decís.'
      : '• Búsqueda web: APAGADA en este turno. Respondé con lo que sabés y aclaralo si el dato la necesitaba; el usuario la prende desde el menú "+" → Herramientas.',
  );
  capacidades.push(
    a.interruptores.investigacion
      ? '• Investigación de datos reales (Instagram, Facebook, Google Maps, tiendas online): DISPONIBLE, el usuario ya la prendió — no hace falta llamar `activar_investigacion`. Puede tardar y consumir créditos: usala sólo para traer datos de una cuenta, marca, tienda o lugar CONCRETOS, y avisá en una línea antes de largarte.'
      : '• Investigación de datos reales (redes, mapas, tiendas): APAGADA. Si hace falta, pedila con `activar_investigacion` (el usuario confirma) o decile que la puede prender desde el menú "+" → Herramientas.',
  );
  capacidades.push(
    a.interruptores.respuestasGraficas
      ? '• Respuestas gráficas: DISPONIBLES (tool mostrar_respuesta_grafica). La mayoría de las respuestas van en texto.'
      : '• Respuestas gráficas: APAGADAS. Respondé siempre en texto; no ofrezcas gráficos.',
  );
  capacidades.push(lineaConfirmacionParaPrompt(a.permisos ?? PERMISOS_POR_DEFECTO));
  lineas.push(`══════════ QUÉ TENÉS HABILITADO AHORA ══════════\n${capacidades.join('\n')}`);

  return lineas.join('\n\n');
}
